import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { api } from '../api';
import { Price } from '../context/CurrencyContext';

const emptyTraveler = (user) => ({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '', passport: '' });

export const TravelBookingDialog = ({ open, onOpenChange, type, offer, count, user }) => {
  const navigate = useNavigate();
  const [travelers, setTravelers] = useState(() => Array.from({ length: count }, (_, i) => (i === 0 ? emptyTraveler(user) : emptyTraveler())));
  const [requests, setRequests] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const update = (i, k, v) => setTravelers((prev) => prev.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post('/travel/bookings', { booking_type: type, offer, travelers: normalizeTravelers(travelers), special_requests: requests || null });
      toast.success(`Reserved! Reference ${res.data.reference}. Our team will confirm & collect payment.`);
      onOpenChange(false);
      navigate('/my-bookings');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not complete the reservation');
    } finally {
      setSubmitting(false);
    }
  };

  if (!offer) return null;
  const title = type === 'flight' ? `${offer.airline} ${offer.flight_number} · ${offer.origin} → ${offer.destination}` : `${offer.hotel_name} · ${offer.room_type}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="travel-booking-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">{type === 'flight' ? 'Passenger details' : 'Guest details'}</DialogTitle>
          <p className="text-sm text-gray-500">{title}</p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          {travelers.map((t, i) => (
            <div key={i} className="rounded-lg border border-gray-200 p-4 space-y-3" data-testid={`traveler-form-${i}`}>
              <p className="text-sm font-semibold text-teal-700">{type === 'flight' ? 'Passenger' : 'Guest'} {i + 1}{i === 0 ? ' (lead contact)' : ''}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="mb-1 block text-xs">Full name (as on ID) *</Label><Input data-testid={`traveler-name-${i}`} value={t.name} onChange={(e) => update(i, 'name', e.target.value)} required minLength={2} className="border-2" /></div>
                <div><Label className="mb-1 block text-xs">{type === 'flight' ? 'Passport number' : 'ID number'}</Label><Input data-testid={`traveler-passport-${i}`} value={t.passport} onChange={(e) => update(i, 'passport', e.target.value)} className="border-2" /></div>
                {i === 0 && (
                  <>
                    <div><Label className="mb-1 block text-xs">Email *</Label><Input data-testid="traveler-email-0" type="email" value={t.email} onChange={(e) => update(i, 'email', e.target.value)} required className="border-2" /></div>
                    <div><Label className="mb-1 block text-xs">Phone *</Label><Input data-testid="traveler-phone-0" type="tel" value={t.phone} onChange={(e) => update(i, 'phone', e.target.value)} required minLength={5} placeholder="+965..." className="border-2" /></div>
                  </>
                )}
              </div>
            </div>
          ))}
          <div>
            <Label className="mb-1 block text-xs">Special requests</Label>
            <Textarea data-testid="travel-requests-input" value={requests} onChange={(e) => setRequests(e.target.value)} rows={2} maxLength={500} className="border-2" placeholder="Meal preference, seat, late check-in..." />
          </div>
          <div className="flex items-center justify-between rounded-lg bg-teal-50 px-4 py-3">
            <div>
              <p className="text-xs text-gray-500">Total · pay at agency / on confirmation</p>
              <Price inr={offer.total_price} className="text-2xl font-bold text-teal-700" testId="travel-dialog-total" />
            </div>
            <Button data-testid="travel-confirm-btn" type="submit" disabled={submitting} className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-6 py-5">
              {submitting ? 'Reserving...' : 'Confirm reservation'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Non-lead travelers inherit lead contact for backend validation
export const normalizeTravelers = (travelers) => travelers.map((t, i) => (i === 0 ? t : { ...t, email: travelers[0].email, phone: travelers[0].phone }));
